import { useState, useEffect } from "react";
import { AlertTriangle, Trash2, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DANGER ZONE — permanent, self-service account deletion.
 * Flow: Preflight check → open modal → accept T&C → send OTP → verify OTP + phrase → confirm delete → sign out.
 */
export default function DeleteAccountSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreflight(null); setAcceptedTerms(false); setOtpSent(false); setOtp(""); setPhrase("");
    (async () => {
      try {
        const { data } = await api.get("/account/delete/preflight");
        setPreflight(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setOpen(false);
      }
    })();
  }, [open]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete/request-otp");
      setOtpSent(true);
      toast.success("OTP sent to your registered email");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete/confirm", {
        otp: otp.trim(),
        confirm_phrase: phrase.trim(),
        accepted_terms: acceptedTerms,
      });
      toast.success("Account permanently deleted");
      // Small delay so the toast is readable, then sign out.
      setTimeout(() => { logout(); navigate("/"); }, 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="nb-card p-6 mt-6 border border-white/10" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={16} className="text-slate-400"/>
          <div className="font-heading text-slate-200 font-semibold text-sm">Delete Account</div>
        </div>
        <div className="text-xs text-slate-400 mb-4">
          If you no longer wish to use Next Billion, you can permanently remove your account and personal data.
          Please withdraw your balance and close any running bots first.
        </div>
        <button
          data-testid="open-delete-account"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 hover:border-red-500/40 hover:bg-red-500/10 text-slate-300 hover:text-red-300 text-xs font-medium transition-colors"
        >
          <Trash2 size={12}/> Delete my account
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !busy && setOpen(false)}/>
          <div className="relative z-10 w-full max-w-lg rounded-2xl border border-red-500/40 bg-[#0F1626] shadow-2xl shadow-red-500/20 max-h-[90dvh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-400"/>
                <div>
                  <div className="font-heading text-white font-semibold">Delete Account Permanently</div>
                  <div className="text-xs text-slate-400">This action is irreversible.</div>
                </div>
              </div>
              <button data-testid="delete-modal-close" onClick={() => !busy && setOpen(false)} className="text-slate-400 hover:text-white"><X size={18}/></button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              {!preflight ? (
                <div className="text-slate-400">Checking your account status…</div>
              ) : !preflight.can_delete ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-300 text-xs">
                    <div className="font-bold mb-1">Account cannot be deleted right now.</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {preflight.reasons.map((r, i) => (<li key={i}>{r}</li>))}
                    </ul>
                  </div>
                  <div className="text-slate-400 text-xs">
                    Please resolve the above blockers first, then come back to delete your account.
                  </div>
                  <button data-testid="delete-close-blocked" onClick={() => setOpen(false)} className="nb-btn-secondary w-full">Close</button>
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-red-200 text-xs space-y-1.5" data-testid="delete-terms">
                    <div className="font-bold uppercase tracking-widest text-red-300 text-[10px]">Terms &amp; Conditions of Deletion</div>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Your account will be <span className="font-bold">permanently deleted</span> from the Next Billion system.</li>
                      <li>Your personal data (name, email, phone, wallet address, KYC documents, profile image) will be erased.</li>
                      <li>You will lose access to all past transactions, referral commissions, and account history — <span className="font-bold">this cannot be recovered.</span></li>
                      <li>Any remaining balance must be withdrawn before deletion. Post-deletion recovery is not possible.</li>
                      <li>Your login credentials (password, PIN, fingerprint) will be revoked immediately.</li>
                      <li>Your referral link and code will stop working; any pending referrals in progress will be lost.</li>
                      <li>Regulatory records (transaction ledger required by law) may be retained anonymously for audit purposes only.</li>
                    </ul>
                  </div>

                  <label className="flex items-start gap-2 text-slate-300 cursor-pointer" data-testid="delete-accept-terms-label">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5"
                      data-testid="delete-accept-terms"
                    />
                    <span className="text-xs">I have read and accept the above terms. I understand this action is <span className="font-bold text-red-300">permanent and cannot be undone</span>.</span>
                  </label>

                  {!otpSent ? (
                    <button
                      data-testid="delete-request-otp"
                      onClick={requestOtp}
                      disabled={busy || !acceptedTerms}
                      className="w-full px-4 py-2.5 rounded-lg border border-red-500/50 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed text-red-300 font-semibold text-sm transition-colors"
                    >
                      {busy ? "Sending OTP…" : "Send OTP to my email"}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-300">
                        OTP sent to your registered email. Check inbox &amp; spam. Valid for 10 minutes.
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-widest">Enter OTP</label>
                        <input
                          data-testid="delete-otp"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                          maxLength={6}
                          className="nb-input mt-1 font-mono text-xl tracking-[0.4em] text-center"
                          placeholder="000000"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-widest">Type &ldquo;{preflight.confirm_phrase}&rdquo; to confirm</label>
                        <input
                          data-testid="delete-phrase"
                          value={phrase}
                          onChange={(e) => setPhrase(e.target.value)}
                          className="nb-input mt-1 font-mono text-sm"
                          placeholder={preflight.confirm_phrase}
                        />
                      </div>
                      <button
                        data-testid="delete-confirm"
                        onClick={confirmDelete}
                        disabled={busy || otp.length < 6 || phrase.trim().toUpperCase() !== preflight.confirm_phrase}
                        className="w-full px-4 py-2.5 rounded-lg border border-red-500/60 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-red-200 font-bold text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 size={14}/> {busy ? "Deleting…" : "Permanently Delete Account"}
                      </button>
                      <button
                        data-testid="delete-cancel"
                        onClick={() => setOpen(false)}
                        disabled={busy}
                        className="w-full text-xs text-slate-400 hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
